import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Reveal from "@/components/base/Reveal";
import BackToAll from "@/components/feature/BackToAll";
import { buildFaqPageSchema } from "@/components/feature/FaqAccordion";
import EnquirySection from "@/components/feature/EnquirySection";
import EnquiryModal from "@/components/feature/EnquiryModal";
import type { EnquiryIntent } from "@/components/feature/EnquiryForm";
import StickyEnquiryBar from "@/components/feature/StickyEnquiryBar";
import { buildBreadcrumbSchema, type BreadcrumbItem } from "@/components/feature/Breadcrumbs";
import Seo, { getAbsoluteUrl } from "@/components/feature/Seo";
import { brand } from "@/mocks/siteData";
import { usePublicFarms } from "@/hooks/usePublicCatalog";
import { farmReopeningLabel, isFarmOpen } from "@/mocks/farms";
import { buildFarmFaqs } from "./farmFaqs";
import FarmDetailHero from "./components/FarmDetailHero";
import FarmDetailOverview from "./components/FarmDetailOverview";
import FarmDetailGallery from "./components/FarmDetailGallery";
import FarmDetailMap from "./components/FarmDetailMap";
import FarmDetailRelated from "./components/FarmDetailRelated";

export default function FarmDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { data: farms, loading, error, reload } = usePublicFarms();
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryIntent, setEnquiryIntent] = useState<EnquiryIntent>("enquire");

  const openEnquiry = (intent: EnquiryIntent) => {
    setEnquiryIntent(intent);
    setEnquiryOpen(true);
  };

  const farm = slug ? farms.find((item) => item.slug === slug) : undefined;

  if (loading && !farm) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-foreground-600">
            <span className="w-12 h-12 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin"></span>
            <p className="text-sm">Loading farm…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!farm) {
    return (
      <div className="min-h-screen bg-background-50 overflow-x-hidden">
        {/* Unknown slug: noindex, so a dead farm URL is never indexed as a copy of the previous page. */}
        <Seo
          title={error ? "Unable to load this farm" : `Farm not found | ${brand.name}`}
          description="We couldn't find that farm. Explore every working farm across the Godavari delta that welcomes visitors with Rythugaru Agri Tourism."
          path={`/farms/${slug ?? ""}`}
          noindex
        />
        <Navbar />
        <main className="w-full px-4 md:px-8 pt-32 pb-24 min-h-[70vh] flex items-center justify-center">
          <Reveal className="text-center max-w-lg">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-plant-line text-3xl"></i>
            </span>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 font-semibold mt-6">
              {error ? "We couldn't load this farm" : "Farm not found"}
            </h1>
            <p className="mt-4 text-foreground-600 leading-relaxed">
              {error
                ? error
                : "The farm you are looking for may have moved or is no longer listed. Explore every farm we work with across the delta instead."}
            </p>
            {error ? (
              <button
                type="button"
                onClick={reload}
                className="mt-8 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                Try again
              </button>
            ) : (
              <Link
                to="/farms"
                className="mt-8 inline-flex items-center gap-2 bg-primary-500 text-background-50 text-sm font-semibold px-7 py-4 rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Browse all farms
                <i className="ri-arrow-right-line"></i>
              </Link>
            )}
          </Reveal>
        </main>
        <Footer />
      </div>
    );
  }

  const faqs = buildFarmFaqs(farm);
  const open = isFarmOpen(farm);
  const reopeningLabel = farmReopeningLabel(farm);
  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Home", to: "/" },
    { label: "Discover Farms", to: "/farms" },
    { label: farm.name },
  ];

  return (
    <div className="min-h-screen bg-background-50 overflow-x-hidden">
      <Seo
        title={
          open
            ? `${farm.name} — Farm Visit in ${farm.village}`
            : `${farm.name} — Reopening Soon in ${farm.village}`
        }
        description={
          open
            ? farm.summary
            : `${farm.name} in ${farm.village}, Konaseema is temporarily closed and ${
                reopeningLabel ? `expected back by ${reopeningLabel}` : "reopening soon"
              } — see the farm, its crops and the visit it will offer once dates reopen.`
        }
        tokens={{ title: farm.name, description: farm.summary }}
        path={`/farms/${farm.slug}`}
        keywords={`${farm.crop} farm, farm visit ${farm.village}, agri tourism Konaseema, ${farm.village} farm experience, meet the farmer`}
        image={farm.image}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": ["TouristAttraction", "Place"],
            name: farm.name,
            description: farm.summary,
            image: farm.image,
            url: getAbsoluteUrl(`/farms/${farm.slug}`),
            touristType: ["Families", "Children", "Students", "Travelers"],
            isAccessibleForFree: false,
            address: {
              "@type": "PostalAddress",
              addressLocality: farm.village,
              addressRegion: "Andhra Pradesh",
              addressCountry: "IN",
            },
            containedInPlace: {
              "@type": "Place",
              name: `${farm.village}, ${farm.district}`,
            },
            provider: {
              "@type": "Organization",
              name: brand.fullName,
            },
          },
          buildFaqPageSchema(faqs),
          buildBreadcrumbSchema(breadcrumbs, getAbsoluteUrl(`/farms/${farm.slug}`)),
        ]}
      />
      <Navbar />
      <main>
        <FarmDetailHero
          farm={farm}
          breadcrumbs={breadcrumbs}
          onNotify={() => openEnquiry("notify")}
        />
        <FarmDetailOverview farm={farm} />
        <FarmDetailGallery farm={farm} />
        <FarmDetailMap farm={farm} />
        <FarmDetailRelated farm={farm} farms={farms} />
        <EnquirySection
          eyebrow={open ? "Plan Your Visit" : "Reopening Soon"}
          heading={
            open
              ? `Plan Your Visit to ${farm.name}`
              : `Be First to Know When ${farm.name} Reopens`
          }
          intro={
            open
              ? `Everything you might want to know before you set foot on ${farm.name} in ${farm.village} — and one place to start planning the trip.`
              : `${farm.name} is temporarily closed and ${
                  reopeningLabel ? `expected back by ${reopeningLabel}` : "reopening soon"
                }, so dates cannot be booked yet. Send an enquiry and we will tell you the moment visits resume.`
          }
          faqs={faqs}
          panelHeading="Enquire About This Farm"
          panelIntro={
            open
              ? `Share a few details about your group and the season you are planning. Our team replies within 24 hours with availability and a clear quote for visiting ${farm.name}.`
              : `Leave your details and we will let you know as soon as ${farm.name} reopens, along with the dates and activities on offer.`
          }
          buttonLabel={open ? "Open Enquiry Form" : "Notify me when it reopens"}
          onEnquire={() => openEnquiry(open ? "enquire" : "notify")}
        />
        <BackToAll to="/farms" label="Back to all farms" icon="ri-plant-line" />
        <StickyEnquiryBar
          title={farm.name}
          eyebrow={open ? `Farm in ${farm.village}` : `Reopening soon in ${farm.village}`}
          onEnquire={() => openEnquiry(open ? "enquire" : "notify")}
        />
        <EnquiryModal
          open={enquiryOpen}
          onClose={() => setEnquiryOpen(false)}
          kind="farm"
          intent={enquiryIntent}
          options={farms.map((item) => ({ value: item.name, label: item.name }))}
          defaultValue={farm.name}
        />
      </main>
      <Footer />
    </div>
  );
}